/*#
# Backup: the whole store, off the host

Every onboarded project's tasks live in virta's log, and an immutable log
doesn't survive losing the host, the Firebase project or the account
(owner, 2026-10-01; board #2479). `virta backup <dir>` copies all of it to
a directory a private git repo can keep, and `virta restore-backup <dir>`
rebuilds a host from it.

- **`events.jsonl`**: every event, verbatim, in log order. Each run appends
  only the events it doesn't hold yet (by id), so the file only grows and a
  git diff is just the new lines.
- **`files/<area>/<path>`**: every stored file a task or showcase names.
  Paths are content hashes, so a copied file is checked against its name.
- **`backup.json`**: the host, the counts, the last log position and the
  event file's SHA-256.

This copy is private: it keeps everything, provenance and addresses
included. (The practices export is the public one.) A restore writes only
the events the host lacks, in their original order, so task numbers come
out the same on an empty host, and then the files. Provenance can't be
restored: the host stamps whoever restores, and each event keeps its own
`by`.

```
virta backup ../virta-backup          # incremental: run it on a schedule
virta restore-backup ../virta-backup --dry
```
*/

/*{ "parent": "Reference", "order": 40 }*/

import type { AttachedFile, EventDraft, VirtaEvent } from './model'
import { DEFAULT_PAGE, type SyncLog } from './log'
import type { VirtaStore } from './store'
import { sha256Hex } from './files'

/** A directory the backup lives in: real files in the CLI, a map in tests. */
export interface BackupDir {
  readText(path: string): string | null
  writeText(path: string, text: string): void
  appendText(path: string, text: string): void
  has(path: string): boolean
  readBytes(path: string): Uint8Array | null
  writeBytes(path: string, bytes: Uint8Array): void
  /** every path under `prefix` */
  list(prefix: string): string[]
}

/** Where stored files come from (the host's `/blob`). */
export interface BlobSource {
  getBlob(area: string, path: string): Promise<Blob | null>
}

/** Where a restore puts them back. */
export interface BlobSink {
  putBlob(
    area: string,
    path: string,
    bytes: BodyInit,
    contentType: string
  ): Promise<unknown>
}

export const BACKUP_EVENTS = 'events.jsonl'
export const BACKUP_MANIFEST = 'backup.json'

/** A file's place in the backup: `files/<area>/<path>`, the area's `:` made safe for every filesystem. */
export const backupFilePath = (area: string, path: string) =>
  `files/${area.replace(/[^\w.-]/g, '-')}/${path}`

/** Every stored file the events name: attachments and showcase pictures, once each. */
export function fileRefsOf(events: readonly VirtaEvent[]): AttachedFile[] {
  const seen = new Map<string, AttachedFile>()
  for (const e of events) {
    const file = (e.payload as { file?: AttachedFile }).file
    if (file?.area && file.path) seen.set(`${file.area}/${file.path}`, file)
  }
  return [...seen.values()]
}

export interface BackupManifest {
  host: string
  updatedAt: string
  events: number
  lastSeq: number
  files: number
  /** SHA-256 of events.jsonl */
  sha256: string
}

export interface BackupResult {
  added: number
  events: number
  filesAdded: number
  files: number
  /** files an event names that the host no longer has */
  missing: string[]
}

/** Every event the log holds, in order. */
async function allEvents(log: SyncLog): Promise<VirtaEvent[]> {
  const out: VirtaEvent[] = []
  let after = 0
  for (;;) {
    const page = await log.pull(after, DEFAULT_PAGE)
    out.push(...page.events)
    const more = page.more ?? page.events.length >= DEFAULT_PAGE
    if (!page.events.length || !more) break
    after = page.events[page.events.length - 1]!.seq
  }
  return out
}

/** The events a backup holds, in its order. */
export function backupEvents(dir: BackupDir): VirtaEvent[] {
  return (dir.readText(BACKUP_EVENTS) ?? '')
    .split('\n')
    .filter((l) => l.trim())
    .map((l) => JSON.parse(l) as VirtaEvent)
}

/**
 * Bring the backup up to date: append the events it lacks (by id), then
 * copy the files it lacks, each checked against the hash its path names.
 */
export async function backup(
  log: SyncLog,
  dir: BackupDir,
  options: { blobs?: BlobSource; host?: string; now?: string } = {}
): Promise<BackupResult> {
  const held = backupEvents(dir)
  const ids = new Set(held.map((e) => e.id))
  const fresh = (await allEvents(log)).filter((e) => !ids.has(e.id))
  if (fresh.length)
    dir.appendText(
      BACKUP_EVENTS,
      fresh.map((e) => JSON.stringify(e)).join('\n') + '\n'
    )
  const events = [...held, ...fresh]
  let filesAdded = 0
  const missing: string[] = []
  const refs = fileRefsOf(events)
  if (options.blobs)
    for (const ref of refs) {
      const to = backupFilePath(ref.area, ref.path)
      if (dir.has(to)) continue
      const blob = await options.blobs.getBlob(ref.area, ref.path)
      if (!blob) {
        missing.push(`${ref.area}/${ref.path}`)
        continue
      }
      const bytes = new Uint8Array(await blob.arrayBuffer())
      // a content-addressed path is its own check
      const want = ref.path.split('.')[0]!
      if (/^[0-9a-f]{64}$/.test(want) && (await sha256Hex(bytes)) !== want)
        throw new Error(`${ref.path}: the host's bytes don't match their hash`)
      dir.writeBytes(to, bytes)
      filesAdded++
    }
  const text = dir.readText(BACKUP_EVENTS) ?? ''
  const manifest: BackupManifest = {
    host: options.host ?? '',
    updatedAt: options.now ?? new Date().toISOString(),
    events: events.length,
    lastSeq: events.reduce((m, e) => Math.max(m, e.seq), 0),
    files: refs.length - missing.length,
    sha256: await sha256Hex(new TextEncoder().encode(text)),
  }
  dir.writeText(BACKUP_MANIFEST, JSON.stringify(manifest, null, 2) + '\n')
  return {
    added: fresh.length,
    events: events.length,
    filesAdded,
    files: manifest.files,
    missing,
  }
}

export interface RestoreBackupResult {
  /** events the host lacked: written (or, dry, to write) */
  events: number
  /** files put back (or, dry, to put back) */
  files: number
  dry: boolean
}

/** How many events one push carries: the host's atomic commit limit. */
const PER_PUSH = 400

/**
 * Rebuild a host from a backup: the events it lacks, in their original
 * order (so numbers come out the same on an empty host), then the files.
 * A second run writes nothing.
 */
export async function restoreBackup(
  store: VirtaStore,
  dir: BackupDir,
  options: { blobs?: BlobSink; dry?: boolean } = {}
): Promise<RestoreBackupResult> {
  const events = backupEvents(dir).sort((a, b) => a.seq - b.seq)
  const drafts: EventDraft[] = events
    .filter((e) => !store.holds(e.id))
    .map((e) => {
      const { seq: _seq, attribution: _stamp, ...draft } = e
      return draft as EventDraft
    })
  const refs = fileRefsOf(events).filter((r) =>
    dir.has(backupFilePath(r.area, r.path))
  )
  if (options.dry)
    return { events: drafts.length, files: refs.length, dry: true }
  for (let i = 0; i < drafts.length; i += PER_PUSH)
    await store.import(drafts.slice(i, i + PER_PUSH))
  let files = 0
  if (options.blobs)
    for (const ref of refs) {
      const bytes = dir.readBytes(backupFilePath(ref.area, ref.path))!
      try {
        await options.blobs.putBlob(
          ref.area,
          ref.path,
          bytes.slice().buffer,
          ref.type
        )
        files++
      } catch (e) {
        // already there: a content-addressed file is the same file
        if (!/\b409\b|exists/i.test(e instanceof Error ? e.message : String(e)))
          throw e
      }
    }
  return { events: drafts.length, files, dry: false }
}
